import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { useContact } from "@/api/queries";
import { Timeline } from "@/components/Timeline";
import { SuppressButton } from "@/components/SuppressButton";
import {
  Card,
  CreatedBy,
  EmptyState,
  ErrorState,
  Field,
  Page,
  Spinner,
} from "@/components/ui";
import { WhatsAppButton } from "@/inbox/WhatsAppButton";
import { SendMailButton } from "@/mail/SendMailButton";
import { CallButton } from "@/telephony/CallButton";
import { NotFound } from "./NotFound";

/**
 * Consent is tri-state and the third state matters.
 *
 * `null` means nobody ever asked — which is every row the legacy import
 * created. Rendering that as "No" would be a lie the seller acts on, and
 * rendering it as "Sí" would be one that lands in front of a regulator
 * (.docs/06 §2). It says "sin registro" because that is what it is.
 */
function Consent({ value }: { value: boolean | null }) {
  const { t } = useTranslation();
  if (value === null) {
    return <span className="text-mute-400">{t("contacts.consentUnknown")}</span>;
  }
  return value ? (
    <span className="text-ink">{t("common.yes")}</span>
  ) : (
    <span className="text-brand-700">{t("common.no")}</span>
  );
}

export function ContactDetail() {
  const { id } = useParams();
  const contactId = Number(id);
  if (!Number.isInteger(contactId) || contactId <= 0) return <NotFound />;
  return <ContactDetailView contactId={contactId} />;
}

function ContactDetailView({ contactId }: { contactId: number }) {
  const { t } = useTranslation();
  const { data, isPending, isError, refetch } = useContact(contactId);

  if (isPending) return <Spinner label={t("contacts.loading")} />;
  if (isError) {
    return (
      <div className="mx-auto max-w-6xl p-6">
        <ErrorState message={t("contacts.loadFailed")} onRetry={() => void refetch()} />
      </div>
    );
  }

  // Switched off by management: on file to be read, with nothing to reach
  // them by — the same as their card on the company screen.
  const disabled = Boolean(data.disabled_at);

  return (
    <Page title={data.name} subtitle={data.job_title || undefined}>
      {disabled ? (
        <p role="status" className="mb-4 border-2 border-rule px-4 py-3 text-sm">
          <span className="tag tag-outline mr-2">
            {t("companyDetail.contactDisabled")}
          </span>
          {t("companyDetail.contactDisabledHint")}
        </p>
      ) : null}
      <CreatedBy
        created={data.created}
        by={data.created_by_agent_name}
        className="mb-4"
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Timeline scope={{ on: "contact", id: contactId }} />
        </div>

        <div className="space-y-4">
          <Card title={t("contacts.phones")}>
            {data.phones.length === 0 ? (
              <EmptyState>{t("contacts.noPhones")}</EmptyState>
            ) : (
              <ul>
                {data.phones.map((phone) => (
                  <li
                    key={phone.id}
                    className="border-b border-rule px-4 py-2 text-sm last:border-0"
                  >
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      {/* Kept alongside the dialler: on a phone the agent may
                          well prefer their own handset, and `tel:` is the only
                          thing that reaches it. */}
                      <a className="font-medium underline" href={`tel:${phone.phone}`}>
                        {phone.phone}
                      </a>
                      {phone.extension ? (
                        <span className="text-mute-600">
                          {t("contacts.extension", { extension: phone.extension })}
                        </span>
                      ) : null}
                      <span className="text-xs text-mute-600">
                        {phone.kind_display}
                      </span>
                      {phone.is_primary ? (
                        <span className="text-xs text-mute-600">
                          {t("contacts.primary")}
                        </span>
                      ) : null}
                      {/* A `wa_id` means WhatsApp has confirmed this number is on
                          the network — the +52/+521 resolution in
                          `whatsapp/phones.py`, not a guess from the digits. */}
                      {phone.wa_id ? (
                        <span className="text-xs text-brand-700">
                          {t("contacts.onWhatsApp")}
                        </span>
                      ) : null}
                    </div>
                    {/* A number on the do-not-call list is not offered at all.
                        `start_call` refuses it server-side either way (.docs/06
                        §4) — this is so the agent never gets that far. */}
                    {disabled ? null : (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {phone.is_suppressed ? (
                          <span className="tag tag-outline text-brand-700">
                            {t("suppress.badge")}
                          </span>
                        ) : (
                          <>
                            {/* `phone`, never `number`: the pk is what puts the
                              call on this contact's — and the company's —
                              timeline. A dialled string would land as an
                              unattached `Call`. */}
                            <CallButton phone={phone.id} label={data.name} />
                            <SuppressButton phone={phone.phone} />
                          </>
                        )}
                        {/* Outside the suppression branch on purpose: the
                          do-not-call list is about calls, and WhatsApp answers
                          to its own consent, which the button reads. */}
                        <WhatsAppButton contact={data} phone={phone} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title={t("contacts.emails")}>
            {data.emails.length === 0 ? (
              <EmptyState>{t("contacts.noEmails")}</EmptyState>
            ) : (
              <ul>
                {data.emails.map((email) => (
                  <li
                    key={email.id}
                    className="flex flex-wrap items-center gap-x-2 gap-y-2 border-b border-rule px-4 py-2 text-sm last:border-0"
                  >
                    {/* Kept as a `mailto:` beside the button, like `tel:`
                        beside the dialler: it is what reaches the seller's
                        own mail app. */}
                    <a className="underline" href={`mailto:${email.email}`}>
                      {email.email}
                    </a>
                    {disabled ? null : (
                      <SendMailButton contact={data} address={email.email} />
                    )}
                    {email.wants_newsletter ? (
                      <span className="text-xs text-mute-600">
                        {t("contacts.newsletter")}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title={t("contacts.consents")}>
            <dl>
              <Field label={t("contacts.consentCalls")}>
                <Consent value={data.consents_to_calls} />
              </Field>
              <Field label={t("contacts.consentRecording")}>
                <Consent value={data.consents_to_recording} />
              </Field>
              <Field label={t("contacts.consentWhatsApp")}>
                <Consent value={data.consents_to_whatsapp} />
              </Field>
              <Field label={t("contacts.consentMarketing")}>
                <Consent value={data.consents_to_marketing} />
              </Field>
            </dl>
          </Card>

          <p className="px-1 text-xs text-mute-600">
            <Link className="underline" to="/prospectos">
              {t("contacts.backToCompanies")}
            </Link>
          </p>
        </div>
      </div>
    </Page>
  );
}
